<script lang="ts">
	// The page a mailed link opens (D18). Today one kind of link exists, the
	// one that finishes an account (UC-25); it acts as the page opens.
	//
	// The token is after the link's #, which a browser never sends to a
	// server, so no Cloudflare or Caddy log ever holds it. The page reads it,
	// takes it off the address bar so it stays in no history, and posts it.

	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import Loading from '../components/Loading.svelte';
	import { message, type Language } from '../i18n/messages';
	import { verifyLink, type LinkResult } from '../owner/password';
	import { navigate } from '../router.svelte';

	interface Props {
		language: Language;
		/** The link signed the new owner in; the app reloads the session. */
		onSignedIn: () => void;
	}

	let { language, onSignedIn }: Props = $props();

	let outcome = $state<LinkResult | null>(null);

	$effect(() => {
		const token = window.location.hash.slice(1);
		window.history.replaceState(window.history.state, '', window.location.pathname);
		void verifyLink(token).then((result) => {
			if (result.kind === 'signedIn') onSignedIn();
			else outcome = result;
		});
	});
</script>

<section class="screen" data-testid="screen-email-link">
	{#if outcome === null}
		<Loading
			size="lg"
			label={message(language, 'loading')}
			caption={message(language, 'linkChecking')}
			testId="email-link-checking"
		/>
	{:else}
		<div class="card" data-testid="email-link-invalid">
			<BrandMark size={26} />
			<div class="words">
				<h1>
					{outcome.kind === 'invalid'
						? message(language, 'linkInvalidTitle')
						: message(language, 'loadFailed')}
				</h1>
				{#if outcome.kind === 'invalid'}
					<p class="instruction">{message(language, 'linkInvalidText')}</p>
				{/if}
			</div>
			<div class="ways">
				<Button
					testId="email-link-sign-in"
					label={message(language, 'signInSubmit')}
					tone="ink"
					size="md"
					onPress={() => navigate('/owner')}
				/>
				<Button
					testId="email-link-create-account"
					label={message(language, 'signInCreateAccount')}
					tone="secondary"
					size="md"
					onPress={() => navigate('/owner/signup')}
				/>
			</div>
		</div>
	{/if}
</section>

<style>
	.screen {
		min-block-size: 100dvh;
		display: grid;
		place-items: center;
		padding: var(--space-9);
		background: var(--surface-page);
	}

	.card {
		inline-size: 100%;
		max-inline-size: 420px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.words {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
		text-wrap: pretty;
	}

	.instruction {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.ways {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-4);
	}
</style>
